import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, CheckCircle, XCircle, Flag, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { api, ReviewDto, ReviewPagedResultDto, ReviewStatus } from '../../services/api';

const STATUS_LABELS: Record<ReviewStatus, string> = {
  0: 'Pending',
  1: 'Approved',
  2: 'Rejected',
  3: 'Flagged',
};

const STATUS_COLORS: Record<ReviewStatus, string> = {
  0: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  1: 'bg-green-100 text-green-800 border-green-200',
  2: 'bg-red-100 text-red-800 border-red-200',
  3: 'bg-orange-100 text-orange-800 border-orange-200',
};

export function ReviewModerationPanel() {
  const [data, setData] = useState<ReviewPagedResultDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [actionLoading, setActionLoading] = useState(false);

  const pageSize = 20;

  const loadReviews = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await api.getPendingReviews(page, pageSize);
      setData(result);
      setSelectedIds(new Set());
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load reviews';
      if (msg.includes('403') || msg.toLowerCase().includes('forbidden')) {
        setError('Access denied. Admin role required to view pending reviews.');
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    loadReviews();
  }, [loadReviews]);

  const handleToggleSelect = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSelectAll = () => {
    if (!data) return;
    if (selectedIds.size === data.reviews.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(data.reviews.map(r => r.id)));
    }
  };

  const handleBulkAction = async (status: ReviewStatus) => {
    if (selectedIds.size === 0) return;
    setActionLoading(true);
    try {
      await api.moderateReviews(Array.from(selectedIds), status);
      await loadReviews();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSingleAction = async (id: number, status: ReviewStatus) => {
    setActionLoading(true);
    try {
      await api.moderateReviews([id], status);
      await loadReviews();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    } catch {
      return iso;
    }
  };

  return (
    <div className="flex-1 flex flex-col overflow-auto">
      {/* Header */}
      <div className="bg-slate-600 text-white p-6">
        <h1 className="text-2xl font-bold">Review Moderation</h1>
        <p className="text-slate-200 mt-1">Review and approve or reject pending user-submitted reviews</p>
      </div>

      <div className="p-6 space-y-4">
        {/* Stats bar */}
        {data && !loading && (
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>
              {data.totalCount === 0
                ? 'No pending reviews'
                : `${data.totalCount} pending review${data.totalCount !== 1 ? 's' : ''}`}
            </span>
            <Button variant="outline" size="sm" onClick={loadReviews} disabled={loading || actionLoading}>
              <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        )}

        {/* Error */}
        {error && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-4">
              <p className="text-red-700 text-sm">{error}</p>
            </CardContent>
          </Card>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && data && data.reviews.length === 0 && (
          <Card>
            <CardContent className="p-12 text-center">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <h3 className="text-lg font-semibold">All caught up!</h3>
              <p className="text-muted-foreground mt-1">No pending reviews to moderate.</p>
            </CardContent>
          </Card>
        )}

        {/* Bulk action bar */}
        {!loading && data && data.reviews.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Pending Reviews</CardTitle>
                {selectedIds.size > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">{selectedIds.size} selected</span>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-green-700 border-green-300 hover:bg-green-50"
                      onClick={() => handleBulkAction(1)}
                      disabled={actionLoading}
                    >
                      <CheckCircle className="w-3.5 h-3.5 mr-1.5" />
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-700 border-red-300 hover:bg-red-50"
                      onClick={() => handleBulkAction(2)}
                      disabled={actionLoading}
                    >
                      <XCircle className="w-3.5 h-3.5 mr-1.5" />
                      Reject
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-orange-700 border-orange-300 hover:bg-orange-50"
                      onClick={() => handleBulkAction(3)}
                      disabled={actionLoading}
                    >
                      <Flag className="w-3.5 h-3.5 mr-1.5" />
                      Flag
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/30">
                      <th className="p-3 text-left w-8">
                        <input
                          type="checkbox"
                          checked={selectedIds.size === data.reviews.length && data.reviews.length > 0}
                          onChange={handleSelectAll}
                          className="rounded"
                          aria-label="Select all"
                        />
                      </th>
                      <th className="p-3 text-left font-medium text-muted-foreground">Reviewer</th>
                      <th className="p-3 text-left font-medium text-muted-foreground">Review</th>
                      <th className="p-3 text-left font-medium text-muted-foreground w-24">Status</th>
                      <th className="p-3 text-left font-medium text-muted-foreground w-36">Submitted</th>
                      <th className="p-3 text-right font-medium text-muted-foreground w-40">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.reviews.map((review: ReviewDto) => (
                      <tr key={review.id} className="border-b hover:bg-muted/20 transition-colors">
                        <td className="p-3">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(review.id)}
                            onChange={() => handleToggleSelect(review.id)}
                            className="rounded"
                            aria-label={`Select review ${review.id}`}
                          />
                        </td>
                        <td className="p-3">
                          <div className="font-medium">{review.reviewerName || review.userId}</div>
                          <div className="text-xs text-muted-foreground">Prompt #{review.promptId}</div>
                        </td>
                        <td className="p-3 max-w-xs">
                          {review.title && (
                            <div className="font-medium mb-0.5 truncate">{review.title}</div>
                          )}
                          <div className="text-muted-foreground line-clamp-2">{review.content}</div>
                        </td>
                        <td className="p-3">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${STATUS_COLORS[review.status]}`}>
                            {STATUS_LABELS[review.status]}
                          </span>
                        </td>
                        <td className="p-3 text-xs text-muted-foreground">
                          {formatDate(review.createdAt)}
                        </td>
                        <td className="p-3">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2 text-green-700 hover:bg-green-50"
                              onClick={() => handleSingleAction(review.id, 1)}
                              disabled={actionLoading}
                              title="Approve"
                            >
                              <CheckCircle className="w-3.5 h-3.5" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2 text-red-700 hover:bg-red-50"
                              onClick={() => handleSingleAction(review.id, 2)}
                              disabled={actionLoading}
                              title="Reject"
                            >
                              <XCircle className="w-3.5 h-3.5" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2 text-orange-700 hover:bg-orange-50"
                              onClick={() => handleSingleAction(review.id, 3)}
                              disabled={actionLoading}
                              title="Flag"
                            >
                              <Flag className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {data.totalPages > 1 && (
                <div className="flex items-center justify-between p-3 border-t">
                  <span className="text-sm text-muted-foreground">
                    Page {data.page} of {data.totalPages}
                  </span>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => p - 1)}
                      disabled={!data.hasPreviousPage || actionLoading}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => p + 1)}
                      disabled={!data.hasNextPage || actionLoading}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
